import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { copyToClipboard } from '../utils/helpers';
import { HydratedPost, Post } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import { PostCard } from './PostCard';
import { getNormalizedPostcardShape } from '../utils/helpers';
import { UsersIcon } from './Icons';

interface PostGridProps {
    posts: HydratedPost[];
    onPostClick: (post: HydratedPost) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onNavigateToProfile: (userId: string) => void;
    onHoldProfile?: (userId: string) => void;
    onLeaveProfile?: () => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void;
    onShare?: (post: HydratedPost) => void;
    onColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void;
    preferredScale?: number;
    onScaleChange?: (scale: number) => void;
    onVouch?: (postId: number) => void;
    onApprovePost?: (postId: number) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onSwipeTransition?: (direction: 'left' | 'right') => void;
    onReportPost?: (postId: number) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    preferredColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    isModalOpen?: boolean;
    showEndMessage?: boolean;
    isNonMemberOfPublicCommunity?: boolean;
    isDemo?: boolean;
    postcardDefault?: string;
    postcardAppearance?: 'original' | 'equipped';
    initialVisibleCount?: number;
    onVisibleCountChange?: (count: number) => void;
    closeInternalModalsTrigger?: number;
    onUpdatePostcardLayout?: (postId: number, layout: 'square' | 'dynamic') => void;
    isLoading?: boolean;
    isHome?: boolean;
    onPin?: (postId: number) => void;
    onUnpin?: (postId: number) => void;
    onReorderPinnedPosts?: (newPinnedOrder: number[]) => void;
    isProfile?: boolean;
    onRetryPostUpload?: (localId: number, startUpload: any) => void;
    onAddToCart?: (post: any) => void;
    postcardGap?: number;
    onPostcardGapChange?: (gap: number) => void;
    onPostDragProgress?: (post: any, progress: number, direction: 'left' | 'right' | null) => void;
}

// Helper to calculate widths in a 24-column grid
const getWidthSpan = (post: HydratedPost, columns: number) => {
    const postType = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.type : post.type;
    const originalText = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.text : post.content.text;
    const bumpCaption = (post.type === 'bump' || post.type === 'repost') ? post.content.text : undefined;
    const textContent = (bumpCaption?.length || 0) > (originalText?.length || 0) ? bumpCaption : originalText;
    
    const origMedia = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.media?.[0] : post.content.media?.[0];
    const linkPreviewObj = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.linkPreview : post.content.linkPreview;
    const firstMedia = origMedia || (postType === 'link' ? (linkPreviewObj ? { ...linkPreviewObj } : undefined) : undefined);
    
    const shape = getNormalizedPostcardShape(post.postcardShape, postType, firstMedia, textContent, post.textSize, columns);

    if (columns === 1) return 60;

    if (columns === 6) {
        if (shape === 'wide' || shape === 'wide_tall') return 20; // 2 cols
        return 10; // 1 col
    }
    if (columns === 5) {
        if (shape === 'wide' || shape === 'wide_tall') return 24; // 2 cols
        return 12; // 1 col
    }
    if (columns === 4) {
        if (shape === 'wide' || shape === 'wide_tall') return 30; // 2 cols
        return 15; // 1 col
    }
    if (columns === 3) {
        if (shape === 'wide' || shape === 'wide_tall') return 40; // 2 cols
        return 20; // 1 col
    }
    if (columns === 2) {
        if (shape === 'wide' || shape === 'wide_tall') return 60; // 2 cols
        return 30; // 1 col
    }
    return 60;
};
const getRowSpan = (post: HydratedPost, columns: number) => {
    const postType = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.type : post.type;
    
    const origMedia = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.media?.[0] : post.content.media?.[0];
    const linkPreviewObj = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.linkPreview : post.content.linkPreview;
    const firstMedia = origMedia || (postType === 'link' ? (linkPreviewObj ? { ...linkPreviewObj } : undefined) : undefined);
    
    const originalText = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.text : post.content.text;
    const bumpCaption = (post.type === 'bump' || post.type === 'repost') ? post.content.text : undefined;
    const textContent = (bumpCaption?.length || 0) > (originalText?.length || 0) ? bumpCaption : originalText;
    const shape = getNormalizedPostcardShape(post.postcardShape, postType, firstMedia, textContent, post.textSize, columns);

    if (shape === 'wide_tall') return 4; // Double the height of wide (4 rows)
    if (shape === 'tall') return 3;
    if (shape === 'wide') return 2; // 2 cols wide, 2 rows tall (2:1 aspect ratio)
    return 2; // Square defaults to 2 rows
};

const calculatePackedLayout = (posts: HydratedPost[], columns: number) => {
    const gridCols = 60;
    const heights = new Array(gridCols).fill(1); // Track next available row for each column
    const unplaced = [...posts];
    const packed: { post: HydratedPost, gridStyle: { gridColumn: string, gridRow: string } }[] = [];

    while (unplaced.length > 0) {
        const minH = Math.min(...heights);
        let startCol = -1;
        for (let i = 0; i < gridCols; i++) {
            if (heights[i] === minH) {
                startCol = i;
                break;
            }
        }
        if (startCol === -1) break;

        let gapWidth = 0;
        for (let i = startCol; i < gridCols; i++) {
            if (heights[i] === minH) gapWidth++;
            else break;
        }

        let foundIdx = -1;
        const lookaheadCount = Math.min(unplaced.length, 10);
        for (let i = 0; i < lookaheadCount; i++) {
            const span = getWidthSpan(unplaced[i], columns);
            if (span <= gapWidth) {
                foundIdx = i;
                break;
            }
        }

        if (foundIdx !== -1) {
            const post = unplaced.splice(foundIdx, 1)[0];
            const span = getWidthSpan(post, columns);
            const rowSpan = getRowSpan(post, columns);
            const actualRow = minH;
            const actualCol = startCol + 1;

            for (let i = startCol; i < startCol + span; i++) {
                heights[i] = actualRow + rowSpan;
            }

            packed.push({
                post,
                gridStyle: {
                    gridColumn: `${actualCol} / span ${span}`,
                    gridRow: `${actualRow} / span ${rowSpan}`
                }
            });
        } else {
            for (let i = startCol; i < startCol + gapWidth; i++) {
                heights[i] = minH + 1;
            }
        }
    }
    return packed;
};

export const PostGrid = ({
    posts,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onHoldProfile,
    onLeaveProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    onShare,
    onVouch,
    onApprovePost,
    onColumnsChange,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    preferredColumns = 1,
    preferredScale = 1,
    onScaleChange,
    isModalOpen = false,
    showEndMessage = false,
    isNonMemberOfPublicCommunity = false,
    isDemo = false,
    onSwipeTransition,
    onReportPost,
    postcardDefault,
    postcardAppearance,
    initialVisibleCount = 24,
    onVisibleCountChange,
    closeInternalModalsTrigger = 0,
    onUpdatePostcardLayout,
    isLoading,
    isHome = false,
    onPin,
    onUnpin,
    onReorderPinnedPosts,
    isProfile = false,
    onRetryPostUpload,
    onAddToCart,
    isShakingMode = false,
    postcardGap = 8,
    onPostcardGapChange,
    onPostDragProgress
}: PostGridProps) => {
    const { currentUser, usersData } = useAuth();
    const { startUpload } = useUpload();
    const actualPostcardAppearance = postcardAppearance ?? (currentUser ? usersData[currentUser]?.postcardAppearance : undefined) ?? 'original';
    const actualPostcardDefault = postcardDefault ?? (currentUser ? usersData[currentUser]?.postcardDefault : undefined) ?? 'classic_postcards';
    const [copied, setCopied] = useState(false);
    const [pinchScale, setPinchScale] = useState(preferredScale);
    const [isGesturing, setIsGesturing] = useState(false);

    useEffect(() => {
        if (!isGesturing) {
            setPinchScale(preferredScale);
        }
    }, [preferredScale, isGesturing]);

    const getColumns = useCallback(() => {
        if (typeof window !== 'undefined') {
            const isDesktop = window.innerWidth >= 1024;
            if (isDesktop) {
                return Math.max(4, Math.min(6, preferredColumns || 4)) as 1 | 2 | 3 | 4 | 5 | 6;
            }
            return Math.max(1, Math.min(6, preferredColumns || 1)) as 1 | 2 | 3 | 4 | 5 | 6;
        }
        return preferredColumns;
    }, [preferredColumns]);

    const [columns, setColumns] = useState(getColumns());
    const [visibleCount, setVisibleCount] = useState(initialVisibleCount || 24);

    useEffect(() => {
        console.log('[PostGrid Debug] preferredColumns:', preferredColumns, 'computed columns:', columns, 'preferredScale:', preferredScale);
    }, [preferredColumns, columns, preferredScale]);

    useEffect(() => {
        if (initialVisibleCount !== undefined && initialVisibleCount !== visibleCount) {
            setVisibleCount(initialVisibleCount);
        }
    }, [initialVisibleCount]);

    useEffect(() => {
        if (onVisibleCountChange) {
            onVisibleCountChange(visibleCount);
        }
    }, [visibleCount, onVisibleCountChange]);

    useEffect(() => {
        setColumns(getColumns());
    }, [preferredColumns, getColumns]);

    useEffect(() => {
        const onResize = () => setColumns(getColumns());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [getColumns]);

    const gridRef = React.useRef<HTMLDivElement>(null);
    const pinchScaleRef = React.useRef<number>(preferredScale);
    const touchStartDistance = React.useRef<number | null>(null);
    const touchStartX = React.useRef<number | null>(null);
    const touchStartY = React.useRef<number | null>(null);
    const initialColumns = React.useRef<number>(columns);
    const initialGapRef = React.useRef<number>(8);
    const hasSwiped = React.useRef<boolean>(false);

    const lastMultiTouchTime = React.useRef<number>(0);
    const isPinchingRef = React.useRef<boolean>(false);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (isModalOpen) return;
        if (e.touches.length > 1) {
            e.stopPropagation();
        }

        hasSwiped.current = false;
        if (e.touches.length === 1) {
            if (Date.now() - lastMultiTouchTime.current > 2000) {
                isPinchingRef.current = false;
            }
            touchStartX.current = e.touches[0].clientX;
            touchStartY.current = e.touches[0].clientY;
        } else if (e.touches.length === 2 && (onColumnsChange || isShakingMode)) {
            lastMultiTouchTime.current = Date.now();
            isPinchingRef.current = true;
            touchStartDistance.current = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            initialColumns.current = columns;
            initialGapRef.current = postcardGap;
            setIsGesturing(true);
        }
    }, [columns, onColumnsChange, isModalOpen, isShakingMode, postcardGap]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (isModalOpen) return;
        if (e.touches.length > 1) {
            e.stopPropagation();
        }

        if (e.touches.length > 1) {
            lastMultiTouchTime.current = Date.now();
            isPinchingRef.current = true;
        }



        if (e.touches.length === 2 && touchStartDistance.current !== null && (onColumnsChange || isShakingMode)) {
            const currentDistance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );

            const ratio = currentDistance / touchStartDistance.current;

            if (isShakingMode) {
                // Pinching out (ratio > 1) expands the gap, pinching in (ratio < 1) shrinks it
                const newGap = Math.min(48, Math.max(0, initialGapRef.current * ratio));
                if (onPostcardGapChange) {
                    onPostcardGapChange(newGap);
                }
                return;
            }

            let newScale = preferredScale * ratio;

            const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
            const minCols = isDesktop ? 4 : 1;
            const maxCols = isDesktop ? 6 : 6;

            if (newScale >= 1.2 && columns < maxCols) {
                const finalCols = Math.min(maxCols, initialColumns.current + 1) as 1 | 2 | 3 | 4 | 5 | 6;

                if (finalCols !== columns && onColumnsChange) {
                    onColumnsChange(finalCols);
                    if (onScaleChange) onScaleChange(1.0);
                    setPinchScale(1.0);
                    pinchScaleRef.current = 1.0;
                    touchStartDistance.current = currentDistance;
                    initialColumns.current = finalCols;
                    return;
                } else if (!onColumnsChange && finalCols !== columns) {
                    setColumns(finalCols);
                    if (onScaleChange) onScaleChange(1.0);
                    setPinchScale(1.0);
                    pinchScaleRef.current = 1.0;
                    touchStartDistance.current = currentDistance;
                    initialColumns.current = finalCols;
                    return;
                }
            } else if (newScale <= 0.8 && columns > minCols) {
                const finalCols = Math.max(minCols, initialColumns.current - 1) as 1 | 2 | 3 | 4 | 5 | 6;

                if (finalCols !== columns && onColumnsChange) {
                    onColumnsChange(finalCols);
                    if (onScaleChange) onScaleChange(1.0);
                    setPinchScale(1.0);
                    pinchScaleRef.current = 1.0;
                    touchStartDistance.current = currentDistance;
                    initialColumns.current = finalCols;
                    return;
                } else if (!onColumnsChange && finalCols !== columns) {
                    setColumns(finalCols);
                    if (onScaleChange) onScaleChange(1.0);
                    setPinchScale(1.0);
                    pinchScaleRef.current = 1.0;
                    touchStartDistance.current = currentDistance;
                    initialColumns.current = finalCols;
                    return;
                }
            }

            newScale = Math.min(1.2, Math.max(0.8, newScale));
            pinchScaleRef.current = newScale;

            if (gridRef.current) {
                gridRef.current.style.transform = `scale(${newScale})`;
                gridRef.current.style.transition = 'none';
            }
        }
    }, [columns, onColumnsChange, onScaleChange, preferredScale, onSwipeTransition, isModalOpen, isShakingMode, onPostcardGapChange, postcardGap]);

    const handleTouchEnd = useCallback(() => {
        if (touchStartDistance.current !== null) {
            lastMultiTouchTime.current = Date.now();
        }
        touchStartDistance.current = null;
        touchStartX.current = null;
        touchStartY.current = null;
        setIsGesturing(false);
        setPinchScale(pinchScaleRef.current);
        if (onScaleChange) onScaleChange(pinchScaleRef.current);
        
        // Longer delay before allowing single-touch swipes again, 
        // but keep the long cooldown check in Move/Start
        setTimeout(() => {
            if (touchStartX.current === null && touchStartDistance.current === null) {
                isPinchingRef.current = false;
            }
        }, 500);
    }, [onScaleChange]);

    const handleTouchCancel = useCallback(() => {
        touchStartDistance.current = null;
        touchStartX.current = null;
        touchStartY.current = null;
        setIsGesturing(false);
        setPinchScale(pinchScaleRef.current);
    }, []);

    const getGridColsClass = () => {
        return 'grid-cols-60 gap-0 p-[2px]';
    };

    // PACKING LOGIC
    const packedPosts = useMemo(() => calculatePackedLayout(posts.slice(0, visibleCount), columns), [posts, visibleCount, columns]);

    // Keep track of the currently selected post ID for keyboard navigation
    const [selectedPostId, setSelectedPostId] = useState<number | null>(null);

    // Reordering state for pinned posts drag-and-drop
    const [draggedPostId, setDraggedPostId] = useState<number | null>(null);
    const [draggedElement, setDraggedElement] = useState<HTMLElement | null>(null);
    const [originalIndex, setOriginalIndex] = useState<number | null>(null);



    // Handle global touch/mouse movement & drops for reordering pinned posts
    const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [dragStartPos, setDragStartPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    useEffect(() => {
        const handleDragStart = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            const postId = detail.postId;
            if (!postId || !onReorderPinnedPosts) return;

            const pinnedIds = usersData[currentUser || '']?.pinnedPosts || [];
            const index = pinnedIds.indexOf(postId);
            if (index !== -1) {
                setDraggedPostId(postId);
                setOriginalIndex(index);
                setDragStartPos({ x: detail.startX, y: detail.startY });
                setDragOffset({ x: 0, y: 0 });

                // Find element of the dragged post
                const elements = document.querySelectorAll('.post-card');
                elements.forEach((el) => {
                    const htmlEl = el as HTMLElement;
                    if (htmlEl.innerHTML.includes(String(postId))) {
                        setDraggedElement(htmlEl);
                    }
                });
            }
        };

        window.addEventListener('pinned-post-drag-start', handleDragStart);
        return () => {
            window.removeEventListener('pinned-post-drag-start', handleDragStart);
        };
    }, [currentUser, usersData, onReorderPinnedPosts]);

    useEffect(() => {
        if (draggedPostId === null) return;

        const handleMove = (clientX: number, clientY: number) => {
            // Apply visual movement offset
            const offsetX = clientX - dragStartPos.x;
            const offsetY = clientY - dragStartPos.y;
            setDragOffset({ x: offsetX, y: offsetY });

            // Find element under pointer
            // Briefly hide pointerEvents to get the element underneath
            const elementUnderPointer = document.elementFromPoint(clientX, clientY);
            if (!elementUnderPointer) return;

            const postCardContainer = elementUnderPointer.closest('[data-post-id]');
            if (postCardContainer) {
                const hitPostId = Number(postCardContainer.getAttribute('data-post-id'));
                if (hitPostId && hitPostId !== draggedPostId) {
                    const hitPost = packedPosts.find(({ post }) => post.id === hitPostId);
                    if (hitPost) {
                    const pinnedIds = usersData[currentUser || '']?.pinnedPosts || [];
                    const isHitPostPinned = pinnedIds.includes(hitPost.post.id);
                    if (isHitPostPinned) {
                        const targetIndex = pinnedIds.indexOf(hitPost.post.id);
                        if (targetIndex !== -1 && targetIndex !== originalIndex) {
                            const newPinned = [...pinnedIds];
                            newPinned.splice(originalIndex!, 1);
                            newPinned.splice(targetIndex, 0, draggedPostId);
                            
                            // Re-anchor drag start position to prevent wild offset jumps during rearrangement
                            const draggedRect = draggedElement?.getBoundingClientRect();
                            const hitRect = postCardContainer.getBoundingClientRect();
                            if (draggedRect && hitRect) {
                                const diffX = hitRect.left - draggedRect.left;
                                const diffY = hitRect.top - draggedRect.top;
                                setDragStartPos(prev => ({
                                    x: prev.x + diffX,
                                    y: prev.y + diffY
                                }));
                            }

                                setOriginalIndex(targetIndex);
                                onReorderPinnedPosts?.(newPinned);
                            }
                        }
                    }
                }
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 0) return;
            handleMove(e.touches[0].clientX, e.touches[0].clientY);
        };

        const handleMouseMove = (e: MouseEvent) => {
            handleMove(e.clientX, e.clientY);
        };

        const handleEnd = () => {
            if (draggedElement) {
                draggedElement.style.opacity = '';
                draggedElement.style.transform = '';
                draggedElement.style.zIndex = '';
                draggedElement.style.pointerEvents = '';
                draggedElement.style.transition = '';
            }
            setDraggedPostId(null);
            setDraggedElement(null);
            setOriginalIndex(null);
            setDragOffset({ x: 0, y: 0 });
        };

        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        window.addEventListener('touchend', handleEnd, { passive: true });
        window.addEventListener('mouseup', handleEnd, { passive: true });
        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchend', handleEnd);
            window.removeEventListener('mouseup', handleEnd);
        };
    }, [draggedPostId, draggedElement, originalIndex, dragStartPos, currentUser, usersData, onReorderPinnedPosts, packedPosts]);

    // Sync selectedPostId when selection changes globally
    useEffect(() => {
        const handleSelectedEvent = (e: Event) => {
            const id = (e as CustomEvent).detail?.postId;
            setSelectedPostId(id);
        };
        const handleDeselectedEvent = (e: Event) => {
            const id = (e as CustomEvent).detail?.postId;
            setSelectedPostId(prev => prev === id ? null : prev);
        };
        window.addEventListener('postcard-selected', handleSelectedEvent);
        window.addEventListener('postcard-deselected', handleDeselectedEvent);
        return () => {
            window.removeEventListener('postcard-selected', handleSelectedEvent);
            window.removeEventListener('postcard-deselected', handleDeselectedEvent);
        };
    }, []);

    // Arrow Key Navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isModalOpen || packedPosts.length === 0) return;

            const isArrow = ['ArrowLeft', 'ArrowRight'].includes(e.key);
            if (!isArrow) return;

            e.preventDefault();

            // Find first postcard if nothing is selected
            if (selectedPostId === null) {
                const firstPost = packedPosts[0].post;
                setSelectedPostId(firstPost.id);
                window.dispatchEvent(new CustomEvent('postcard-force-select', { detail: { postId: firstPost.id } }));
                return;
            }

            const currentIndex = packedPosts.findIndex(p => p.post.id === selectedPostId);
            if (currentIndex === -1) {
                // Fallback to first post
                const firstPost = packedPosts[0].post;
                setSelectedPostId(firstPost.id);
                window.dispatchEvent(new CustomEvent('postcard-force-select', { detail: { postId: firstPost.id } }));
                return;
            }

            let targetPost = null;

            if (e.key === 'ArrowLeft') {
                if (currentIndex > 0) {
                    targetPost = packedPosts[currentIndex - 1].post;
                } else {
                    onSwipeTransition?.('right');
                }
            } else if (e.key === 'ArrowRight') {
                if (currentIndex < packedPosts.length - 1) {
                    targetPost = packedPosts[currentIndex + 1].post;
                } else {
                    onSwipeTransition?.('left');
                }
            }

            if (targetPost) {
                setSelectedPostId(targetPost.id);
                window.dispatchEvent(new CustomEvent('postcard-force-select', { detail: { postId: targetPost.id } }));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedPostId, packedPosts, isModalOpen, onSwipeTransition]);

    useEffect(() => {
        const handleScroll = () => {
            if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 500) {
                setVisibleCount(prev => {
                    if (prev >= posts.length) return prev;
                    // Load posts in larger batches (e.g. 24) to prevent continuous re-rendering
                    // which causes iOS momentum scrolling to stutter and get stuck
                    return Math.min(posts.length, prev + 24);
                });
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [posts.length, columns]);

    const handleShareClick = () => {
        const shareUrl = `${window.location.origin}/profile/${currentUser}`;
        copyToClipboard(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const isEndOfFeed = visibleCount >= posts.length && showEndMessage;

    return (
        <div
            className="flex flex-col"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            <LayoutGroup id="post-grid">
                <motion.div
                    ref={gridRef}
                    className={`grid ${getGridColsClass()} max-w-full overflow-x-hidden`}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(60, minmax(0, 1fr))',
                        transform: isShakingMode ? 'none' : `scale(${pinchScale})`,
                        transformOrigin: 'center top',
                        transition: (isGesturing && touchStartDistance.current !== null) ? 'none' : 'transform 0.15s ease-out',
                        willChange: 'transform'
                    }}
                >
                    <AnimatePresence initial={false}>
                        {packedPosts.map(({ post, gridStyle }) => (
                            <motion.div
                                key={`${post.id}-${post.type}`}
                                layout={draggedPostId === post.id ? false : "position"}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                data-post-id={post.id}
                                transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 30,
                                    layout: { duration: 0.4, ease: "easeOut" }
                                }}
                                style={{
                                    gridColumn: gridStyle.gridColumn,
                                    gridRow: gridStyle.gridRow,
                                    position: 'relative',
                                    transform: draggedPostId === post.id ? `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(0.85)` : undefined,
                                    zIndex: draggedPostId === post.id ? 100 : undefined,
                                    boxShadow: draggedPostId === post.id ? '0 20px 25px -5px rgb(0 0 0 / 0.5), 0 8px 10px -6px rgb(0 0 0 / 0.5)' : undefined,
                                    pointerEvents: draggedPostId === post.id ? 'none' : undefined,
                                    touchAction: draggedPostId === post.id ? 'none' : undefined
                                }}
                            >
                                <PostCard
                                    post={post}
                                    onPostClick={onPostClick}
                                    onLike={onLike}
                                    onBump={onBump}
                                    onBookmark={onBookmark}
                                    onEdit={onEdit}
                                    isNonMemberOfPublicCommunity={isNonMemberOfPublicCommunity}
                                    onDelete={onDelete}
                                    onNavigateToProfile={onNavigateToProfile}
                                    onHoldProfile={onHoldProfile}
                                    onLeaveProfile={onLeaveProfile}
                                    onNavigateToHashtag={onNavigateToHashtag}
                                    onNavigateToCommunity={onNavigateToCommunity}
                                    onShare={onShare}
                                    onVouch={onVouch}
                                    onApprovePost={onApprovePost}
                                    onMuteUser={onMuteUser}
                                    onBlockUser={onBlockUser}
                                    onGrantAdmin={onGrantAdmin}
                                    onDeleteUser={onDeleteUser}
                                    onReportPost={onReportPost}
                                    onUpdatePostcardLayout={onUpdatePostcardLayout}
                                    animatingLikes={animatingLikes}
                                    animatingBumps={animatingBumps}
                                    animatingBookmarks={animatingBookmarks}
                                    viewerPostcardDefault={actualPostcardDefault}
                                    viewerPostcardAppearance={actualPostcardAppearance}
                                    galleryAnimation={galleryAnimation}
                                    postBlurOpacity={postBlurOpacity}
                                    columns={columns}
                                    isModalOpen={isModalOpen}
                                    isDemo={isDemo}
                                    closeInternalModalsTrigger={closeInternalModalsTrigger}
                                    externalGridStyle={gridStyle}
                                    isHome={isHome}
                                    onPin={onPin}
                                    onUnpin={onUnpin}
                                    isProfile={isProfile}
                                    onAddToCart={onAddToCart}
                                    isShakingMode={isShakingMode}
                                    postcardGap={postcardGap}
                                    onPostDragProgress={onPostDragProgress}
                                />
                                {/* Failed upload overlay — shown directly on top of the post */}
                                {post.uploadFailed && (
                                    <div
                                        className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-[1.5rem] overflow-hidden"
                                        style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
                                    >
                                        <span className="text-xl">⚠️</span>
                                        <p className="text-white text-[10px] font-bold text-center px-2 leading-tight" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>Upload failed</p>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onRetryPostUpload?.((post as any).localId || post.id, startUpload); }}
                                            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 active:scale-95 text-white text-[10px] font-black rounded-full transition-all"
                                        >
                                            Retry
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onDelete(post.id); }}
                                            className="px-3 py-1 bg-red-600/80 hover:bg-red-700 active:scale-95 text-white text-[10px] font-bold rounded-full transition-all"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {/* End-of-feed card — inside the grid so it moves with the scaled content */}
                    {isEndOfFeed && !isLoading && (
                        <div style={{ gridColumn: '1 / 61', gridRow: 'auto', padding: '4px' }}>
                            <div
                                className="flex flex-col items-center justify-center p-6 py-8 text-center rounded-2xl shadow-lg relative overflow-hidden animate-fade-in transition-transform hover:scale-[1.01] group border border-white/5"
                                style={{ backgroundColor: 'var(--app-theme-bg)' }}
                            >
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mb-4 shadow-2xl">
                                    <UsersIcon className="w-10 h-10 sm:w-12 sm:h-12 text-blue-400" />
                                </div>
                                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 text-raised">hmm.. seems like you need more friends..</h3>
                                <p className="text-white text-base sm:text-lg mb-4">tell your folks to join! this app is all about community so bring yours</p>

                                {currentUser && (
                                    <button onClick={handleShareClick} className="text-xs text-white text-raised bg-white/20 px-3 py-1.5 rounded-full border border-white flex items-center gap-2 hover:bg-black/20 transition-colors">
                                        <span className="font-semibold">Share your page!</span>
                                        {copied && <span className="ml-2 text-sm text-green-400">Copied</span>}
                                    </button>
                                )}

                                {currentUser !== 'free_entry' && (
                                    <div className="mt-4 pt-3 text-center">
                                        <p className="text-[10px] text-white uppercase font-bold tracking-widest mb-4">You can make a ride or die friend right here</p>
                                        <div className="flex flex-wrap justify-center gap-3 mb-6">
                                            <a href="https://cash.app/$uzomaki" target="_blank" rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 py-2 bg-[#00D632]/10 hover:bg-[#00D632]/20 text-[#00D632] rounded-full text-xs font-black transition-all border border-[#00D632]/20 shadow-lg shadow-[#00D632]/5">
                                                <span className="text-sm font-bold"></span> Find em here
                                            </a>
                                            <a href="https://venmo.com/ejiketion" target="_blank" rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 py-2 bg-[#008CFF]/10 hover:bg-[#008CFF]/20 text-[#008CFF] rounded-full text-xs font-black transition-all border border-[#008CFF]/20 shadow-lg shadow-[#008CFF]/5">
                                                or also here
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </motion.div>
            </LayoutGroup>
        </div>
    );
};
